
import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import Spinner from '../ui-elements/Spinner'
import RedAlert from '../ui-elements/RedAlert'
import { subscribe } from '../utils/pubsub'
import GreenAlert from '../ui-elements/GreenAlert'

const SEARCH_DEBOUNCE_MS = 400



type Props = {
  questionId?: string
  checklistId?: string
  setQuestionAnswer: React.Dispatch<React.SetStateAction<any>>
  defaultValue: null | string | string[]
  handleChangeSelectMultiple: AnyFunction
  thisQuestion: ObjectStringKeyAnyValue
}

function QuestionSelectFromUploadedList({
  questionId,
  checklistId,
  setQuestionAnswer,
  defaultValue,
  handleChangeSelectMultiple,
  thisQuestion,
}: Props) {

  const {
    t,
    sendMessageToWebsocket
  } = useContext(GlobalContext)

  const questionFormat = thisQuestion.QuestionFormat
  const questionOptionsFromListId = thisQuestion.QuestionOptionsFromListId

  const [listDetails, setListDetails] = useState<null | ObjectStringKeyAnyValue>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [searchText, setSearchText] = useState('')
  const [searchResults, setSearchResults] = useState<null | ObjectStringKeyAnyValue[]>(null)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  useEffect(() => {
    if (!questionOptionsFromListId) return

    setListDetails(null)
    setErrorMessage(null)

    const requestedId = questionOptionsFromListId

    const unsubscribe = subscribe('fetchOptionListDetailsResponse', (data: ObjectStringKeyAnyValue) => {
      if (data.id !== requestedId) return
      unsubscribe()
      if (data.error) {
        setErrorMessage(data.error)
        return
      }
      setListDetails(data)
    })

    sendMessageToWebsocket(JSON.stringify({
      action: 'uploadedOptionLists',
      subAction: 'fetch_option_list_details',
      Id: `${requestedId}`,
    }))

    return unsubscribe
  }, [questionOptionsFromListId])

  useEffect(() => {
    if (!searchText.trim()) {
      setSearchResults(null)
      setSearchError(null)
      setIsSearching(false)
      return
    }

    let unsubscribe: (() => void) | null = null

    const debounceTimeout = setTimeout(() => {
      setIsSearching(true)
      setSearchError(null)

      unsubscribe = subscribe('searchOptionListResponse', (data: ObjectStringKeyAnyValue) => {
        if (data.id !== questionOptionsFromListId) return
        if (unsubscribe) unsubscribe()
        setIsSearching(false)
        if (data.error) {
          setSearchError(data.error)
          return
        }
        setSearchResults(data.options || [])
      })

      sendMessageToWebsocket(JSON.stringify({
        action: 'uploadedOptionLists',
        subAction: 'search_option_list',
        Id: questionOptionsFromListId,
        searchText: searchText.trim(),
      }))
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(debounceTimeout)
      if (unsubscribe) unsubscribe()
    }
  }, [searchText, questionOptionsFromListId])

  const isOptionSelected = (option: ObjectStringKeyAnyValue) => {
    if (questionFormat === 'SelectMultiple') {
      return Array.isArray(defaultValue) && defaultValue.includes(option.Id)
    }
    return defaultValue === option.Id
  }

  const selectOption = (option: ObjectStringKeyAnyValue) => {
    if (questionFormat === 'SelectMultiple') {
      handleChangeSelectMultiple(option.Id)
    } else {
      setQuestionAnswer(option.Id)
    }
  }

  return <div className={`w-full flex flex-col gap-2 items-center justify-center`}>

    {!listDetails && !errorMessage && <Spinner />}

    {errorMessage && <RedAlert>{errorMessage}</RedAlert>}

    {listDetails && <>
      <GreenAlert size='small'>Showing options for uploaded list: "{listDetails.displayName}" ({listDetails.rowCount} {t('QuestionPages.QuestionSelect.options')})</GreenAlert>

      <input
        type='text'
        value={searchText}
        onChange={(e: any) => setSearchText(e.target.value)}
        placeholder={t('QuestionPages.QuestionSelect.searchPlaceholder')}
        className={`w-full border border-gray-300 rounded px-3 py-2 text-sm`}
      />

      {isSearching && <Spinner size='small' />}

      {searchError && <RedAlert>{searchError}</RedAlert>}

      {!isSearching && !searchError && searchResults && searchResults.length < 1 &&
        <RedAlert>{t('QuestionPages.QuestionSelect.noSearchResults')}</RedAlert>}

      {!isSearching && searchResults && searchResults.length > 0 && <div className={`w-full flex flex-col gap-2`}>
        {searchResults.map((option: ObjectStringKeyAnyValue) => {
          return <div key={option.Id}>
            <Button
              onClick={() => selectOption(option)}
              text={option.displayText}
              variant={isOptionSelected(option) ? 'primary' : 'white'}
              size={'big'}
              wordWrap={true}
            />
          </div>
        })}
      </div>}
    </>}

  </div>
}
export default QuestionSelectFromUploadedList
